import React, { type JSX } from 'react';
import { CustomModal, ModalActions } from './CustomModal';
import { useConfirm } from '@fiji/common/src/hooks';
import { DeleteModal } from '.';
import { Stack, Checkbox, DialogContent, Typography } from '@mui/material';
import { InfoListItem } from '@brightlayer-ui/react-components';
import { useSelectedIds } from '../hooks';
import { CustomTransComponent } from '@fiji/web/src/components';
import { useGetDeviceListMutation } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { GATEWAY_VENDOR } from '@fiji/common/src/constants';
type MultiItemsDeleteModalProps = {
    isOpen: boolean;
    header: string;
    isLoading: boolean;
    handleModalClose: () => void;
    handleConfirmDelete: () => void;
    deleteSuccess: boolean;
    handleIdsToBeDeleted: any;
    selectedIds: string[];
    filteredData: any;
    titleKey: string;
    titleKey1?: string;
    subtitleKey: string;
    deleteModalHeader: string;
    keyToTraverse: string;
    deleteModalDescription: string;
    deleteButtonText?: string;
};

export const DeleteDeviceModal = (props: MultiItemsDeleteModalProps): JSX.Element => {
    const modalRef = React.useRef<any>(null);

    const [selectedIds, setSelectedIds, selectionHandler] = useSelectedIds();
    const { isVisible, onCancel, onClick, onConfirm } = useConfirm(props.handleConfirmDelete);
    // FIJI-70879: Only block deletion for non-Starfish gateway children. BLG300 (Starfish) supports
    // C2D commands and its child devices can be deleted directly.
    const isAllGatewayChild = props.filteredData.every(
        (item: any) =>
            item.wrapperDeviceType === 'Child' &&
            item.parentDeviceType === 'Gateway' &&
            item.vendorTypeName?.toLowerCase() !== GATEWAY_VENDOR.starfish
    );

    const isAllPXDChild = props.filteredData.every(
        (item: any) => item.wrapperDeviceType === 'Child' && item.vendorTypeName === 'Dashboard'
    );

    const isPXDChild = props.filteredData.some(
        (item: any) => item.wrapperDeviceType === 'Child' && item.vendorTypeName === 'Dashboard'
    );

    const isGateway = props.filteredData.some((item: any) => item.wrapperDeviceType === 'Gateway');
    const isPXDGateway = props.filteredData.some(
        (item: any) => item.wrapperDeviceType === 'Gateway' && item.vendorTypeName === 'Dashboard'
    );
    const initailState = props.filteredData.map((item: any) => item.id);
    const initailStatePXDChild = props.filteredData.filter(
        (item: any) => item.vendorTypeName === 'Dashboard' && item.wrapperDeviceType === 'Child'
    );

    const [selectedDeviceIds, setSelectedDeviceIds] = React.useState<any>(initailState);
    const [step, setStep] = React.useState(isPXDChild ? 1 : 2);
    const [getDeviceList, { isLoading: isDeviceListLoading }] = useGetDeviceListMutation();
    const [list, setList] = React.useState<any>([]);

    React.useEffect(() => {
        if (props.deleteSuccess) onCancel();
    }, [props.deleteSuccess]);

    React.useEffect(() => {
        if (props?.selectedIds) {
            setSelectedIds(props.selectedIds);
        }
    }, [props?.selectedIds]);

    React.useEffect(() => {
        const selectedIdsClone = JSON.parse(JSON.stringify(list));
        const listIds = selectedIdsClone?.map((item: any) => item.id);

        if (listIds.length) {
            setSelectedIds(listIds);
        }
    }, [list]);

    React.useEffect(() => {
        if (selectedIds) {
            props.handleIdsToBeDeleted(selectedIds);
        }
    }, [selectedIds]);

    React.useEffect(() => {
        setStep(isPXDChild ? 1 : 2);
    }, [props?.filteredData, isPXDChild]);

    const handleOnPress = (deviceId: string): any => {
        const selectedDeviceIdsClone = [...selectedDeviceIds];
        const userIdIndex = selectedDeviceIdsClone.findIndex((id: string) => id === deviceId);
        if (userIdIndex !== -1) {
            selectedDeviceIdsClone.splice(userIdIndex, 1);
        } else {
            selectedDeviceIdsClone.push(deviceId);
        }
        setSelectedDeviceIds(selectedDeviceIdsClone);
    };

    const getCancelAction = (): ModalActions => ({
        key: 'cancel',
        label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
        close: true,
        variant: 'outlined',
        handleClick: (): void => {
            props.handleModalClose();
            setSelectedIds(props?.selectedIds);
        },
    });

    const isGatewayDevice = (item: any): boolean => item.wrapperDeviceType === 'Gateway';
    const isStandaloneDevice = (item: any): boolean => item.wrapperDeviceType === 'Standalone';
    const isNonPXDChildDevice = (item: any): boolean =>
        item.wrapperDeviceType === 'Child' && item.vendorTypeName !== 'Dashboard';

    const getList = async (): Promise<void> => {
        if (!props?.filteredData) return;

        const parentIds = props?.filteredData.filter(isGatewayDevice).map((item: any) => item.id);
        if (!parentIds.length) {
            const filterNonPxdbChild = props?.filteredData.filter(
                (obj: any) => !initailStatePXDChild.some((obj2: any) => obj.id === obj2.id)
            );
            setList(filterNonPxdbChild);
            return;
        }

        const result: any = await getDeviceList({
            body: {
                page: 0,
                size: 25,
                filters: { parentId: parentIds },
            },
        });

        const nonPXDChildDevices = props?.filteredData.filter(isNonPXDChildDevice);
        const nonAttachedChildren = nonPXDChildDevices.filter((item: any) => !parentIds.includes(item.parentId));
        const filteredTopLevelDevices = props?.filteredData.filter(
            (item: any) => isGatewayDevice(item) || isStandaloneDevice(item)
        );
        const allFilterValue = [...filteredTopLevelDevices, ...nonAttachedChildren];

        const allDevices = allFilterValue.map((item: any) => {
            if (!isGatewayDevice(item)) {
                return item;
            }

            const childDevices = result.data?.data?.records.filter((childItem: any) => childItem.parentId === item.id);
            return { ...item, data: childDevices };
        });

        setList(allDevices);
    };

    React.useEffect(() => {
        void getList();
    }, [props?.filteredData]);

    const modalActions: ModalActions[] = [
        getCancelAction(),
        {
            key: 'continue',
            label: <CustomTransComponent translationKey={'COMMON:CONTINUE'} />,
            variant: 'contained',
            disabled: Boolean(!selectedIds?.length) || isDeviceListLoading,
            handleClick: (): void => {
                if (step === 1) {
                    setStep(2);
                    return;
                }

                props.handleModalClose();
                onClick();
            },
        },
    ];

    const modalChildActions: ModalActions[] = [getCancelAction()];

    const gatewayChildRestrictionActions: ModalActions[] = [
        getCancelAction(),
        {
            key: 'continue',
            label: <CustomTransComponent translationKey={'COMMON:CONTINUE'} />,
            variant: 'contained',
            disabled: true,
            handleClick: (): void => {},
        },
    ];

    const getGroupPath = (item: any): string[] => {
        const groupPathClone = [...item]?.reverse();
        return groupPathClone;
    };

    const showWarnings = (): any => (
        <CustomModal
            ref={modalRef}
            actions={!isAllPXDChild ? modalActions : modalChildActions}
            type="primary"
            header={props?.header}
            isOpen={props?.isOpen}
            isLoading={isDeviceListLoading}
        >
            <DialogContent className="padding-0">
                <Typography variant="body1" data-testid="delete-device-description">
                    {!isAllPXDChild ? (
                        <CustomTransComponent
                            translationKey={
                                'DEVICE_MANAGEMENT:DASHBOARD_CHILD_DEVICE_DELETE_WITH_OTHER_DEVICES_DESCRIPTION'
                            }
                        />
                    ) : (
                        <CustomTransComponent
                            translationKey={'DEVICE_MANAGEMENT:DASHBOARD_CHILD_DEVICE_DELETE_DESCRIPTION'}
                        />
                    )}
                </Typography>
                <Stack my={2} className="max-height-225 overflow-y-auto" data-testid="delete-popup-scrollbar">
                    {initailStatePXDChild?.map((item: any) => (
                        <InfoListItem
                            key={item.id}
                            className="cursor-pointer padding-y-8"
                            dense
                            style={{ marginTop: 10, marginBottom: 16, marginLeft: -70 }}
                            title={
                                <Stack>
                                    <Typography variant="body1" fontWeight={'600'}>
                                        {item?.[props.titleKey]}
                                    </Typography>
                                    {props.titleKey1 && (
                                        <Typography variant="body2" fontSize={'14px'}>
                                            <CustomTransComponent translationKey={'COMMON:MODEL'} />#{' '}
                                            {item?.[props.titleKey1]}
                                        </Typography>
                                    )}
                                </Stack>
                            }
                            subtitle={[
                                ...(item?.parentDeviceName ? [item?.parentDeviceName] : []),
                                ...getGroupPath(item?.[props?.subtitleKey] ?? []),
                            ]}
                            subtitleSeparator={'<'}
                            ripple
                        />
                    ))}
                </Stack>
            </DialogContent>
        </CustomModal>
    );

    const getCheckboxIcon = (dataItem: any, item: any): JSX.Element => (
        <Checkbox
            disabled
            checked={selectedIds.includes(item?.id)}
            onChange={(): void => {
                handleOnPress(dataItem?.id);
            }}
            style={{ alignSelf: 'flex-start', marginTop: -20 }}
        />
    );

    const showGatewayChildRestriction = (): any => (
        <CustomModal
            ref={modalRef}
            actions={gatewayChildRestrictionActions}
            type="primary"
            header={props?.header}
            isOpen={props?.isOpen}
        >
            <DialogContent className="padding-0">
                <Typography variant="body1" fontWeight={600} data-testid="gateway-child-restriction-header">
                    <CustomTransComponent
                        translationKey={'DEVICE_MANAGEMENT:GATEWAY_CHILD_DELETE_RESTRICTION_HEADER'}
                    />
                </Typography>
                <Typography variant="body1" data-testid="gateway-child-restriction-desc" sx={{ mt: 1 }}>
                    <CustomTransComponent translationKey={'DEVICE_MANAGEMENT:GATEWAY_CHILD_DELETE_RESTRICTION_DESC'} />
                </Typography>
                <Stack my={2} className="max-height-225 overflow-y-auto" data-testid="gateway-child-restriction-list">
                    {props.filteredData?.map((item: any) => (
                        <InfoListItem
                            key={item.id}
                            className="cursor-pointer padding-y-8"
                            dense
                            style={{ marginTop: 10, marginBottom: 16, marginLeft: -70 }}
                            title={
                                <Stack>
                                    <Typography variant="body1" fontWeight={'600'}>
                                        {item?.[props.titleKey]}
                                    </Typography>
                                    {props.titleKey1 && (
                                        <Typography variant="body2" fontSize={'14px'}>
                                            <CustomTransComponent translationKey={'COMMON:MODEL'} />#{' '}
                                            {item?.[props.titleKey1]}
                                        </Typography>
                                    )}
                                </Stack>
                            }
                            subtitle={[
                                ...(item?.parentDeviceName ? [item?.parentDeviceName] : []),
                                ...getGroupPath(item?.[props?.subtitleKey] ?? []),
                            ]}
                            subtitleSeparator={'<'}
                            ripple
                        />
                    ))}
                </Stack>
            </DialogContent>
        </CustomModal>
    );

    const showDeviceList = (): any => (
        <CustomModal
            ref={modalRef}
            actions={modalActions}
            type="primary"
            header={props?.header}
            isOpen={props?.isOpen}
            isLoading={isDeviceListLoading}
        >
            <DialogContent className="padding-0">
                <Typography variant="body1" data-testid="delete-device-description">
                    {isGateway ? (
                        <CustomTransComponent translationKey={'DEVICE_MANAGEMENT:DASHBOARD_MAIN_DESC'} />
                    ) : (
                        <CustomTransComponent translationKey={'DEVICE_MANAGEMENT:MULTI_ITEMS_MODAL_DESCRIPTION'} />
                    )}
                    {isPXDGateway && (
                        <Typography variant="body1" fontWeight={600}>
                            <CustomTransComponent translationKey={'DEVICE_MANAGEMENT:DASHBOARD_BOLD_DESC'} />
                        </Typography>
                    )}
                </Typography>

                <Stack my={2} className="max-height-225 overflow-y-auto" data-testid="delete-popup-scrollbar">
                    {list?.map((item: any) => (
                        <>
                            <InfoListItem
                                key={item.id}
                                className="cursor-pointer padding-y-8"
                                dense
                                style={{ marginBottom: 16, marginLeft: -20 }}
                                title={
                                    <Stack>
                                        <Typography variant="body1" fontWeight={'600'}>
                                            {item.name}
                                        </Typography>
                                        {props.titleKey1 && (
                                            <Typography variant="body2" fontSize={'14px'}>
                                                <CustomTransComponent translationKey={'COMMON:MODEL'} />#{' '}
                                                {item?.[props.titleKey1]}
                                            </Typography>
                                        )}
                                    </Stack>
                                }
                                subtitle={[
                                    ...(item?.parentDeviceName ? [item?.parentDeviceName] : []),
                                    ...getGroupPath(item?.[props?.subtitleKey] ?? []),
                                ]}
                                subtitleSeparator={'<'}
                                ripple
                                icon={
                                    <Checkbox
                                        checked={selectedIds.includes(item?.id)}
                                        disabled={item.wrapperDeviceType === 'Child'}
                                        style={{ alignSelf: 'flex-start', marginTop: -10 }}
                                        onChange={selectionHandler(item[props.keyToTraverse])}
                                    />
                                }
                            />
                            {item?.data?.map((dataItem: any) => (
                                <InfoListItem
                                    key={dataItem.name}
                                    title={
                                        <Stack>
                                            <Typography variant="body1" fontWeight={'600'}>
                                                {dataItem.name}
                                            </Typography>
                                            {props.titleKey1 && (
                                                <Typography variant="body2" fontSize={'14px'}>
                                                    <CustomTransComponent translationKey={'COMMON:MODEL'} />#{' '}
                                                    {dataItem?.[props.titleKey1]}
                                                </Typography>
                                            )}
                                        </Stack>
                                    }
                                    subtitle={[
                                        ...(item?.name ? [item?.name] : []),
                                        ...getGroupPath(dataItem?.[props?.subtitleKey] ?? []),
                                    ]}
                                    subtitleSeparator={'<'}
                                    style={{ marginBottom: 8, marginLeft: 5 }}
                                    icon={getCheckboxIcon(dataItem, item)}
                                />
                            ))}
                        </>
                    ))}
                </Stack>
            </DialogContent>
        </CustomModal>
    );

    if (isAllGatewayChild) {
        return <>{showGatewayChildRestriction()}</>;
    }

    const isWarningStep = step === 1;

    return (
        <>
            {isWarningStep ? showWarnings() : showDeviceList()}
            <DeleteModal
                isVisible={isVisible}
                confirmClick={onConfirm}
                onCancel={onCancel}
                isLoading={props?.isLoading}
                header={props?.deleteModalHeader}
                description={props?.deleteModalDescription}
                deleteButtonText={props?.deleteButtonText}
            />
        </>
    );
};
